import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { createUserFormSchemaWithMessages, type CreateUserPayload, type CreateUserFormData } from '../../schemas/user.schema';
import { useCreateUser } from '@hooks/users';
import { useTranslation } from '@hooks/useTranslation';
import { useServerErrors } from '@hooks/useServerErrors';
import { ApiError } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Form } from '../Form';
import { useFormContext, type FormApi } from '../Form/FormProvider';
import { buildRoleOptions } from './constants';
import { canAssignRole } from './canEditUser';
import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';
import { DEFAULT_PHONE_PREFIX, isBlankPhoneNumber, isFieldStaffRole, isFulfilmentRole } from '../../constants';
import { StaffDetailsFields } from '../EditUser/StaffDetailsFields';
import { SlingFields } from '../EditUser/SlingFields';
import type { SlingPosition } from '@api/users';
import { PhoneInput } from './PhoneInput';
import { OptionSelect } from '../Form/OptionSelect';
import { USER_SERVER_ERROR_TARGETS, USER_BASE_ERROR_TARGETS, type UserErrorTarget } from './serverErrorTargets';

interface CreateUserFormProps {
  onSuccess: () => void;
  onPendingChange?: (isPending: boolean) => void;
  currentUser?: CurrentUser;
  slingPositions: SlingPosition[];
}

export function CreateUserForm({ onSuccess, onPendingChange, currentUser, slingPositions }: CreateUserFormProps) {
  const createUser = useCreateUser();

  useEffect(() => {
    onPendingChange?.(createUser.isPending);
    return () => onPendingChange?.(false);
  }, [createUser.isPending, onPendingChange]);
  const { t, getSection } = useTranslation();
  const formRef = useRef<FormApi | null>(null);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  useServerErrors<UserErrorTarget>({ error: submitError, formRef,
    mapServerFieldToTargets: USER_SERVER_ERROR_TARGETS, baseErrorTargets: USER_BASE_ERROR_TARGETS });

  const rolesTranslations = getSection<Record<string, string>>('roles');

  const roleOptions = buildRoleOptions(rolesTranslations).filter(option =>
    canAssignRole(currentUser, option.value)
      && (option.value !== 'developer' || can(currentUser, CAPABILITIES.DEV_TOOLS_WRITE)));
  const schema = createUserFormSchemaWithMessages({
    nameRequired: t('form.validation.name_required'),
    nameMaxLength: t('form.validation.name_max_length'),
    emailRequired: t('form.validation.email_required'),
    emailInvalid: t('form.validation.email_invalid'),
    phoneInvalid: t('form.validation.phone_invalid'),
    roleRequired: t('form.validation.role_required'),
    confirmationStatusRequired: t('form.validation.confirmation_status_required'),
    accessLevelRequired: t('form.validation.access_level_required'),
    nricRequired: t('form.validation.nric_required'),
    nricInvalid: t('form.validation.nric_invalid'),
    slingUserIdRequired: t('form.validation.sling_user_id_required'),
    slingPositionRequired: t('form.validation.sling_position_required'),
  });

  const handleSubmit = async (values: CreateUserFormData) => {
    setSubmitError(null);
    try {
      const payload: CreateUserPayload = {
        name: values.name.trim(),
        email: values.email.trim(),
        mobile_number: isBlankPhoneNumber(values.mobile_number) ? undefined : values.mobile_number,
        role: values.role,
        ...(isFieldStaffRole(values.role) ? {
          employment_type: values.employment_type,
          confirmation_status: values.confirmation_status || undefined,
          access_level: values.access_level || undefined,
          nric: values.nric.trim(),
          vehicle_number: values.vehicle_number.trim() || undefined,
        } : {}),
        ...(isFulfilmentRole(values.role) ? {
          sling_link: { sling_user_id: values.sling_user_id.trim(), position_id: values.sling_position_id },
        } : {}),
      };
      const response = await createUser.mutateAsync(payload);
      const { dismiss } = toast({
        description: <span className="flex min-w-0 items-center gap-1">
          <Link to="/users/$id/edit" params={{ id: String(response.user.id) }} className="truncate underline" onClick={() => dismiss()}>
            {response.user.name || response.user.email}
          </Link>
          <span className="shrink-0">{t('create.saved_label')}</span>
        </span>,
        duration: 10_000,
      });
      onSuccess();
    } catch (error) {
      if (error instanceof ApiError && error.fieldErrors) {
        setSubmitError(error);
        return;
      }
      toast({ variant: 'destructive', description: error instanceof Error ? error.message : t('create.error') });
    }
  };

  return (
    <Form
      formRef={formRef}
      schema={schema}
      defaultValues={{
        name: '',
        email: '',
        mobile_number: DEFAULT_PHONE_PREFIX,
        role: undefined,
        employment_type: 'full_time',
        confirmation_status: '',
        access_level: '',
        nric: '',
        vehicle_number: '',
        sling_user_id: '',
        sling_position_id: '',
      }}
      onSubmit={handleSubmit}
      isPending={createUser.isPending}
      className="flex flex-col gap-8 px-8 pt-6 font-inter"
    >
      <CreateUserFields roleOptions={roleOptions} slingPositions={slingPositions} />

      <div className="-mx-8 border-t border-dinnerplate-white px-8 py-6">
        <div className="flex justify-end">
          <Form.SubmitButton mode="create" label={t('create.button_label')} />
        </div>
      </div>
    </Form>
  );
}

function CreateUserFields({ roleOptions, slingPositions }: {
  roleOptions: ReturnType<typeof buildRoleOptions>;
  slingPositions: SlingPosition[];
}) {
  const { t } = useTranslation();
  const { form } = useFormContext();

  return (
    <>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <Form.FieldRow name="name" label={t('form.labels.name')}>
          {(field, { invalid }) => (
            <Input
              variant="form"
              value={field.state.value as string}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t('form.placeholders.name')}
              aria-invalid={invalid || undefined}
            />
          )}
        </Form.FieldRow>

        <Form.FieldRow name="email" label={t('form.labels.email')}>
          {(field, { invalid }) => (
            <Input
              variant="form"
              type="email"
              value={field.state.value as string}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t('form.placeholders.email')}
              aria-invalid={invalid || undefined}
            />
          )}
        </Form.FieldRow>

        <Form.FieldRow
          name="mobile_number"
          label={t('form.labels.mobile_number')}
          marker="optional"
          markerLabel={t('form.optional')}
        >
          {(field, { invalid }) => (
            <PhoneInput value={field.state.value as string} onChange={field.handleChange} invalid={invalid} />
          )}
        </Form.FieldRow>

        <Form.FieldRow name="role" label={t('form.labels.role')}>
          {(field, { invalid }) => (
            <OptionSelect
              field={field}
              options={roleOptions}
              label={t('form.labels.role')}
              placeholder={t('form.placeholders.select_access_level')}
              invalid={invalid}
            />
          )}
        </Form.FieldRow>
      </div>

      <form.Subscribe selector={(state) => (state.values as CreateUserFormData).role}>
        {(role) => (
          <>
            <StaffDetailsFields FieldRow={Form.FieldRow} headingSize="sm" savedNric={null} hidden={!isFieldStaffRole(role)} />
            <SlingFields positions={slingPositions} headingSize="sm" hidden={!isFulfilmentRole(role)} />
          </>
        )}
      </form.Subscribe>
    </>
  );
}
